import { useEffect } from 'react'
import { business } from '../config/business'

interface SeoProps {
  title: string
  description: string
  path: string
  image?: string
  schema?: Record<string, unknown> | Array<Record<string, unknown>>
}

const upsertMeta = (selector: string, attribute: string, value: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    const [key, keyValue] = attribute.split('=')
    element.setAttribute(key, keyValue)
    document.head.append(element)
  }
  element.setAttribute('content', value)
}

export function Seo({ title, description, path, image = '/og.png', schema }: SeoProps) {
  useEffect(() => {
    const fullTitle = title.includes(business.name)
      ? title
      : `${title} | ${business.name}`
    const canonicalUrl = new URL(path, business.siteUrl).toString()
    const imageUrl = new URL(image, business.siteUrl).toString()

    document.title = fullTitle
    upsertMeta('meta[name="description"]', 'name=description', description)
    upsertMeta('meta[property="og:title"]', 'property=og:title', fullTitle)
    upsertMeta('meta[property="og:description"]', 'property=og:description', description)
    upsertMeta('meta[property="og:type"]', 'property=og:type', 'website')
    upsertMeta('meta[property="og:url"]', 'property=og:url', canonicalUrl)
    upsertMeta('meta[property="og:image"]', 'property=og:image', imageUrl)
    upsertMeta('meta[name="twitter:card"]', 'name=twitter:card', 'summary_large_image')
    upsertMeta('meta[name="twitter:title"]', 'name=twitter:title', fullTitle)
    upsertMeta('meta[name="twitter:description"]', 'name=twitter:description', description)
    upsertMeta('meta[name="twitter:image"]', 'name=twitter:image', imageUrl)

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }
    canonical.href = canonicalUrl

    const id = 'page-schema'
    document.getElementById(id)?.remove()
    if (schema) {
      const script = document.createElement('script')
      script.id = id
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(schema)
      document.head.append(script)
    }

    return () => document.getElementById(id)?.remove()
  }, [description, image, path, schema, title])

  return null
}
